import toast from "react-hot-toast";
import PageHeader from "@/components/PageHeader";
import TabNavigation from "@/components/tabs/TabNavigation";
import KebabMenu, { KebabMenuItem } from "@/components/menus/KebabMenu";
import { Artwork } from "@/components/ArtworkSlot";
import humanizeKind from "@/components/TipTap/humanizeKind";
import buildTrackTabs from "../trackTabs";

interface LyricsSection {
  kind: string | null;
  text: string;
}

interface LyricsShowProps {
  track: {
    id: number;
    title: string;
    artwork_attached: boolean;
  };
  artwork: Artwork;
  sections: LyricsSection[];
  lyrics_with_headers: string;
  lyrics_without_headers: string;
}

const copyToClipboard = async (text: string, successMessage: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch {
    toast.error("Failed to copy");
  }
};

const Show: React.FC<LyricsShowProps> = ({
  track,
  artwork,
  sections,
  lyrics_with_headers,
  lyrics_without_headers,
}) => {
  const kebabItems: KebabMenuItem[] = [
    {
      text: "Copy with headers",
      onClick: () =>
        copyToClipboard(lyrics_with_headers, "Lyrics copied with headers"),
    },
    {
      text: "Copy without headers",
      onClick: () => copyToClipboard(lyrics_without_headers, "Lyrics copied"),
    },
  ];

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      <PageHeader
        backLink={{ href: "/tracks", text: "Back to all tracks" }}
        title={track.title}
        primaryAction={{
          href: `/tracks/${track.id}/lyrics/edit`,
          text: "Edit",
        }}
        artwork={{
          trackId: track.id,
          uploaded: track.artwork_attached,
          thumbnailUrl: artwork.thumbnail_url,
          originalUrl: artwork.original_url,
          alt: `${track.title} artwork`,
        }}
      />
      <TabNavigation
        tabs={buildTrackTabs(track.id, "lyrics")}
      />
      <div className="dark:bg-cream-950 relative flex w-full flex-col gap-8 rounded-2xl bg-white p-6 shadow md:p-10">
        {sections.length === 0 ? (
          <p className="text-cream-600 dark:text-cream-400 text-sm">
            No lyrics have been added yet.
          </p>
        ) : (
          <>
            <div className="absolute right-2 top-2 md:right-4 md:top-4">
              <KebabMenu items={kebabItems} />
            </div>
            {sections.map((section, index) => (
              <div
                key={`${section.kind ?? "untagged"}-${index}`}
                className="flex flex-col gap-2"
              >
                {section.kind && (
                  <h2 className="text-cream-700 dark:text-cream-300 font-serif text-lg font-semibold tracking-wide">
                    [{humanizeKind(section.kind)}]
                  </h2>
                )}
                <p className="text-cream-900 dark:text-cream-100 font-sans whitespace-pre-wrap">
                  {section.text}
                </p>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Show;
